/**
 * Tipos do tour guiado de onboarding (indicadores na sidebar + modal de
 * boas-vindas + passo a passo dentro de cada modulo).
 *
 * Estado vive na tabela `user_onboarding_state` (jsonb), uma linha por
 * usuario (sem tenant_id, sem RLS — mesmo padrao de user_dashboard_layouts).
 *
 * - `dismissed`: fecha so o modal de boas-vindas. Indicadores da sidebar
 *   continuam aparecendo ate cada modulo ser percorrido ou pulado.
 * - `dismissedAll`: setado pelo botao "Nao mostrar dicas" do modal — esconde
 *   o modal E todos os indicadores da sidebar, independente de completed.
 * - `completed`: keys dos tours ja percorridos (Proximo ate o fim) ou
 *   pulados (botao Pular / Esc).
 */

/**
 * Rodada 1: so o tour do Dashboard. Rodada 2 (2026-08-06) adiciona os 4
 * modulos do nucleo operacional: Clientes, Processos, Tarefas e Agenda.
 * Rodada 3 (2026-08-06) adiciona os demais itens de primeiro nivel da
 * sidebar: Financeiro, Documentos, Relatorios, Intimacoes e Configuracoes.
 * "Caixa do portal" ficou de fora — tela fina demais pra um tour de 4+
 * passos com ancoras estaveis (so titulo, filtro de status e tabela sao
 * incondicionais; aprovar/recusar e paginacao dependem de haver dado).
 */
export const TOUR_KEYS = [
  'dashboard',
  'clientes',
  'processos',
  'tarefas',
  'agenda',
  'financeiro',
  'documentos',
  'relatorios',
  'intimacoes',
  'configuracoes',
] as const;
export type TourKey = (typeof TOUR_KEYS)[number];

export function isTourKey(value: unknown): value is TourKey {
  return typeof value === 'string' && (TOUR_KEYS as readonly string[]).includes(value);
}

export interface OnboardingState {
  dismissed: boolean;
  dismissedAll: boolean;
  completed: TourKey[];
}

export const DEFAULT_ONBOARDING_STATE: OnboardingState = {
  dismissed: false,
  dismissedAll: false,
  completed: [],
};

/**
 * Normaliza um valor jsonb bruto (vindo do banco, possivelmente ausente ou
 * de um shape antigo) para um OnboardingState valido. Nunca lanca erro.
 */
export function normalizeOnboardingState(raw: unknown): OnboardingState {
  if (!raw || typeof raw !== 'object') return { ...DEFAULT_ONBOARDING_STATE };
  const v = raw as Record<string, unknown>;
  const completed = Array.isArray(v.completed)
    ? v.completed.filter(isTourKey)
    : [];
  return {
    dismissed: typeof v.dismissed === 'boolean' ? v.dismissed : false,
    dismissedAll: typeof v.dismissedAll === 'boolean' ? v.dismissedAll : false,
    completed,
  };
}
